/**
 * KpiTile — единый компактный стандарт KPI-карточки.
 *
 * Использование:
 *   <KpiTile label="Compliance" value="94%" tone="success" />
 *   <KpiTile label="Тестов" value="1 / 1" hint="из 27" />
 *   <KpiTile label="Health" value={48} tone="danger" big />
 *   <KpiTile icon={Users} label="Активных" value={51} />        // Lucide icon верх-право
 *
 * Размеры (стандарт Tremor / material-tailwind / Ashby):
 *   - default: padding 12px, value 22px, label 11px (uppercase)
 *   - big:     padding 18px, value 36px, label 12px (uppercase)
 *
 * Props:
 *   - label  : string (обязательно) — uppercase подпись внизу карточки
 *   - value  : string | number — главное значение
 *   - hint   : string — подсказка под label мелким шрифтом
 *   - labelTitle : string (опц.) — текст hover-tooltip подписи; по умолчанию сама label
 *   - tone   : 'success' | 'warning' | 'danger' | 'info' | 'neutral' | undefined
 *              цвет value-числа. Нейтральные счётчики оставлять без tone.
 *   - icon   : Lucide-component (опц.) — монохромная иконка в верхнем-правом
 *              углу, цвет = currentColor родителя (мутный). НЕ цветные emoji
 *              (см. CLAUDE.md «Иконки — правила»).
 *   - big    : boolean — увеличенный variant для главной метрики дашборда
 *   - dim    : boolean — opacity 0.6 (для disabled/loading-look)
 *   - testId : string — data-testid на корне плитки (для unit-тестов)
 *   - delta  : number — изменение к прошлому периоду; рисуется значком со
 *              стрелкой рядом со значением. Ноль и null значка не дают.
 *   - deltaTone : 'good' | 'bad' | 'neutral' — что означает это изменение ДЛЯ
 *              ЭТОЙ метрики. Знак числа сам по себе ничего не значит: рост
 *              открытых вакансий нейтрален, рост просрочек — плохо.
 *   - footer : node — блок под подписью (спарклайн, мини-легенда). Прижимается
 *              к низу плитки, чтобы в ряду все футеры стояли на одной линии.
 *   - sentenceLabel : boolean — подпись обычным регистром в две строки вместо
 *              uppercase в одну. Для длинных русских подписей, которые капсом
 *              обрезаются многоточием.
 *   - onClick : функция — делает плитку НАСТОЯЩЕЙ кнопкой (`<button>`), а не
 *              кликабельным `div`: клавиатура, фокус и озвучивание скринридером
 *              приходят от элемента, а не дописываются руками. Плитка и без
 *              onClick подсвечивается на hover (`hover-lift`), поэтому выглядела
 *              кликабельной ещё когда никуда не вела — приёмка 07.09.2026.
 *   - active : boolean — плитка, чей список сейчас открыт (`aria-pressed`).
 *
 * icon принимает И компонент (`icon={Users}` — контракт), И уже готовый элемент
 * (`icon={<Users size={14}/>}`). Второе раньше роняло весь экран React #130
 * «got object» (CohortTab/AIPipelineDashboard, 2026-07-09) — теперь оба варианта
 * поддержаны через isValidElement.
 */
import { isValidElement } from 'react'
import { TrendingDown, TrendingUp } from 'lucide-react'

const TONE_COLOR = {
    success: 'var(--color-success)',
    warning: 'var(--color-warning)',
    danger: 'var(--color-danger)',
    info: 'var(--color-info)',
    neutral: 'var(--color-text-muted)',
}

const DELTA_COLOR = {
    good: 'var(--color-success-text)',
    bad: 'var(--color-danger-text)',
    neutral: 'var(--color-text-muted)',
}

const TONE_TEXT = {
    success: 'var(--color-success-text)',
    warning: 'var(--color-warning-text)',
    danger: 'var(--color-danger-text)',
    info: 'var(--color-info-text)',
    neutral: 'var(--color-text-muted)',
}

/**
 * `flat` — тихий вариант для сводки над рабочей доской: без подъёма на hover и
 * без цветной линии 3px сверху. Семантику несут цвет числа и иконка. Плитка там
 * стоит рядом с воронкой и не должна спорить с ней за внимание; на дашбордах,
 * где плитка сама себе контент, поведение по умолчанию не меняется.
 */
export default function KpiTile({
    label, value, hint, tone, icon: Icon, big = false, compact = false, flat = false, dim = false, testId,
    delta = null, deltaTone = 'neutral', footer = null, sentenceLabel = false, labelTitle,
    onClick = null, active = false,
}) {
    const padding = big ? '18px 16px' : (compact || flat) ? '8px 10px' : '12px 14px'
    const valueSize = big ? 36 : (compact || flat) ? 16 : 22
    const labelSize = big ? 12 : (compact || flat) ? 10 : 11
    const color = tone ? TONE_TEXT[tone] : 'var(--color-text)'
    const iconSize = big ? 18 : (compact || flat) ? 14 : 16
    const mixedCaseLabel = compact || flat || sentenceLabel
    const hasDelta = Number.isFinite(delta) && delta !== 0
    const DeltaIcon = delta > 0 ? TrendingUp : TrendingDown

    const valueNode = (
        <div
            className="numeric-ticker"
            style={{
                fontSize: valueSize,
                fontWeight: 800,
                color,
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                paddingRight: Icon ? iconSize + 8 : 0,
                letterSpacing: 'var(--tracking-display)',
            }}
            title={typeof value === 'string' ? value : undefined}
        >
            {value ?? '—'}
        </div>
    )

    // Кликабельная плитка — кнопка. Сбросы (`font`, `textAlign`, `width`) нужны
    // потому, что кнопка приносит собственные умолчания браузера: без них
    // подпись центрируется, а шрифт уезжает на системный.
    const Root = onClick ? 'button' : 'div'
    const interactive = onClick
        ? {
            type: 'button',
            onClick,
            'aria-pressed': active,
            style: {
                cursor: 'pointer', textAlign: 'left', width: '100%',
                font: 'inherit', color: 'inherit',
            },
        }
        : {}
    return (
        <Root
            className={flat ? 'card' : 'card hover-lift'}
            data-testid={testId}
            {...interactive}
            style={{
                ...(interactive.style || {}),
                padding,
                display: 'flex',
                flexDirection: 'column',
                gap: (compact || flat) ? 2 : 4,
                opacity: dim ? 0.6 : 1,
                minHeight: big ? 80 : flat ? 46 : compact ? 48 : 64,
                position: 'relative',
                borderTop: (tone && !flat) ? `3px solid ${TONE_COLOR[tone]}` : undefined,
                transition: flat
                    ? 'border-color 200ms ease'
                    : 'transform 250ms cubic-bezier(0.2,0,0,1), box-shadow 250ms cubic-bezier(0.2,0,0,1), border-color 200ms ease',
            }}
        >
            {Icon && (
                <span
                    style={{
                        position: 'absolute',
                        top: big ? 16 : (compact || flat) ? 8 : 12,
                        right: big ? 16 : (compact || flat) ? 8 : 12,
                        color: 'var(--color-text-muted)',
                        opacity: 0.6,
                        display: 'inline-flex',
                    }}
                    aria-hidden="true"
                >
                    {isValidElement(Icon) ? Icon : <Icon size={iconSize} />}
                </span>
            )}
            {/* Обёртка появляется ТОЛЬКО со значком изменения. Без неё
                `.numeric-ticker` остаётся прямым flex-элементом колонки и
                тянется на всю ширину плитки — именно это заставляет работать
                обрезку длинного значения многоточием. Безусловная обёртка
                превращала его в inline-block по содержимому: длинные значения
                вылезали за карточку. */}
            {hasDelta ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                    {valueNode}
                    <span
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                            fontSize: 12, fontWeight: 600, color: DELTA_COLOR[deltaTone] || DELTA_COLOR.neutral,
                        }}
                    >
                        <DeltaIcon size={12} aria-hidden="true" />
                        {Math.abs(delta)}
                    </span>
                </div>
            ) : valueNode}
            {/* В compact-варианте label рендерится mixed-case без letter-
                spacing — это даёт ~25% больше символов на ту же ширину
                плитки и убирает «...»-обрезку («Средний балл тестов»
                полностью помещается вместо «СРЕДНИЙ БАЛЛ ...»). До двух
                строк (line-clamp 2, line-height 1.15) — высота карточки
                почти не растёт. Title-атрибут даёт hover-tooltip с полной
                подписью для длинных меток. */}
            <div
                title={labelTitle ?? label}
                style={{
                    fontSize: labelSize,
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: mixedCaseLabel ? 'none' : 'uppercase',
                    letterSpacing: mixedCaseLabel ? 0 : '0.04em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: mixedCaseLabel ? 2 : 1,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: mixedCaseLabel ? 1.15 : 1.2,
                    wordBreak: 'break-word',
                }}
            >
                {label}
            </div>
            {hint && (
                <div className="text-rel-xxs text-muted" style={{ fontWeight: 500 }}>
                    {hint}
                </div>
            )}
            {/* marginTop: auto — в ряду плиток подписи бывают в одну и в две
                строки; без этого футеры встают на разной высоте и ряд рвётся. */}
            {footer && <div style={{ marginTop: 'auto', paddingTop: 6 }}>{footer}</div>}
        </Root>
    )
}

export function pctToTone(pct) {
    if (pct === null || pct === undefined) return 'neutral'
    if (pct >= 80) return 'success'
    if (pct >= 60) return 'warning'
    return 'danger'
}
